import { useElementSize } from '@lighthouse/bui'
import type { IconBlockAbstract, VariableADTvalue } from '@lighthouse/core'
import {
    fieldConvertValue,
    getImageFullUrlInApplication,
    getTextColorStyle,
    IconRender,
    useApplicationContext,
    useAppThemeContext,
    useFlowLayoutContext,
    useMultipleFloatBoxBlockContext,
    useUnifiedTap
} from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import React, { memo, useCallback } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useCustomViewBlockContext, useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

interface IconBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: IconBlockAbstract
    isFloatChildren?: boolean
    onClickEvent?: () => void
}

const SCxContainer = styled(AnimationDecorators)``
const IconBlock: React.FunctionComponent<IconBlockProps> = ({ blockData, isFloatChildren, onClickEvent, ...rest }) => {
    const {
        id,
        title,
        config: {
            breakPoint: {
                size: { width: iconWidth },
                icon,
                animation,
                design: { color }
            },
            anchor
        }
    } = blockData
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { interactionProps, reference } = useMultipleFloatBoxBlockContext()
    // ################################# 解析颜色变量 #################################
    const { useParseVariableWithSubscription } = useFlowLayoutContext()
    const { record } = useCustomViewBlockContext()

    const parseColorVariable = useParseVariableWithSubscription(color?.variable)
    const parseColor = useCallback(
        (v: VariableADTvalue) => {
            const field = parseColorVariable(v, { viewRecord: record })
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        },
        [parseColorVariable, record]
    )
    // ################################# 解析颜色变量 #################################
    const { ref, width } = useElementSize<HTMLDivElement>()

    const { appId } = useApplicationContext()

    const { theme } = useAppThemeContext()

    const unifiedTap = useUnifiedTap(onClickEvent)

    return (
        <SCxContainer
            ref={mergeRefs([ref, reference])}
            blockId={id}
            blockName={title}
            animation={animation}
            id={effectiveAnchorId}
            {...interactionProps.getReferenceProps(unifiedTap)}
            {...rest}
        >
            <IconRender
                size={(iconWidth.unit === 'px' && iconWidth.size && Number(iconWidth.size)) || width || ref.current?.clientWidth}
                isCompact
                readonly
                value={icon?.value ? icon : { type: 'icon', value: 'SmileCircleIconLibrary' }}
                color={getTextColorStyle(color, theme.palettes, parseColor).color}
                onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
            />
        </SCxContainer>
    )
}

export default memo(IconBlock)
